import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Skeleton,
  ConfirmModal,
  SuccessModal,
  ErrorModal,
  Input,
  Select,
  TextArea,
} from '../../components/ui';
import { adminAPI } from '../../services/adminApi';
import {
  Send,
  People,
  FilterList,
  CheckCircle,
  PersonSearch,
  Group,
  Campaign,
  PhoneIphone,
  TouchApp,
} from '../../mui-icons';

const PushCampaignPanel = ({ canEdit }) => {
  const [mode, setMode] = useState('compose');
  const [pushFilters, setPushFilters] = useState([]);
  const [navActions, setNavActions] = useState([]);
  const [recipientType, setRecipientType] = useState('all_with_token');
  const [selectedFilter, setSelectedFilter] = useState('subscribers');
  const [selectedUserIds, setSelectedUserIds] = useState('');
  const [recipientCount, setRecipientCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [pushForm, setPushForm] = useState({
    title: '',
    body: '',
    action: 'open_app',
    personalize: false,
  });
  const [sending, setSending] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [resultModal, setResultModal] = useState({ open: false, data: null });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [filtersRes, navRes] = await Promise.all([
          adminAPI.getPushFilters(),
          adminAPI.getPushNavigationActions(),
        ]);
        if (filtersRes.data?.filters) setPushFilters(filtersRes.data.filters);
        if (navRes.data?.actions?.length) {
          setNavActions(navRes.data.actions);
          setPushForm((prev) => ({
            ...prev,
            action: prev.action || navRes.data.actions[0].action,
          }));
        }
      } catch (e) {
        console.error('Erro ao carregar metadados push:', e);
      }
    };
    loadMeta();
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await adminAPI.getPushCampaignHistory({
        page: historyPagination.page,
        limit: historyPagination.limit,
      });
      if (response.data) {
        setHistory(response.data.campaigns || []);
        if (response.data.pagination) {
          setHistoryPagination((prev) => ({
            ...prev,
            total: response.data.pagination.total,
          }));
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPagination.page, historyPagination.limit]);

  useEffect(() => {
    if (mode === 'history') loadHistory();
  }, [mode, loadHistory]);

  const getRecipientsConfig = useCallback(() => {
    if (recipientType === 'all_with_token') {
      return { type: 'all_with_token' };
    }
    if (recipientType === 'filter') {
      return { type: 'filter', filter: selectedFilter };
    }
    const ids = selectedUserIds
      .split(',')
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !Number.isNaN(id) && id > 0);
    return { type: 'selected', user_ids: ids };
  }, [recipientType, selectedFilter, selectedUserIds]);

  const countRecipients = async () => {
    setCountLoading(true);
    setRecipientCount(null);
    try {
      const response = await adminAPI.countPushRecipients(getRecipientsConfig());
      if (response.data) {
        setRecipientCount({
          total: response.data.total_recipients,
          description: response.data.description,
        });
      }
    } catch (error) {
      setRecipientCount({ total: 0, error: error.message });
    } finally {
      setCountLoading(false);
    }
  };

  const selectedActionLabel =
    navActions.find((a) => a.action === pushForm.action)?.label || pushForm.action;

  const validateForm = () => {
    if (!pushForm.title.trim()) return 'Título é obrigatório';
    if (!pushForm.body.trim()) return 'Mensagem é obrigatória';
    if (!pushForm.action) return 'Selecione a tela ao tocar';
    if (recipientType === 'selected') {
      const ids = selectedUserIds.split(',').filter((id) => id.trim());
      if (ids.length === 0) return 'Informe ao menos um ID';
    }
    return null;
  };

  const handleConfirmSend = async () => {
    setConfirmModal(false);
    setSending(true);
    try {
      const response = await adminAPI.sendPushBroadcast({
        title: pushForm.title.trim(),
        body: pushForm.body.trim(),
        action: pushForm.action,
        recipients: getRecipientsConfig(),
        personalize: pushForm.personalize,
      });
      setResultModal({
        open: true,
        data: {
          success: true,
          campaign_id: response.data?.campaign_id,
          total: response.data?.total_recipients,
          sent: response.data?.sent_count,
          failed: response.data?.failed_count,
          errors: response.data?.errors || [],
          action_label: response.data?.action_label,
        },
      });
      setPushForm({ title: '', body: '', action: pushForm.action, personalize: false });
      setRecipientCount(null);
    } catch (error) {
      setResultModal({
        open: true,
        data: { success: false, error: error.message },
      });
    } finally {
      setSending(false);
    }
  };

  const openCampaignDetail = async (id) => {
    setSelectedCampaign(id);
    try {
      const res = await adminAPI.getPushCampaignDetail(id);
      setCampaignDetail(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const filtersByGroup = pushFilters.reduce((acc, f) => {
    const g = f.group || 'Outros';
    if (!acc[g]) acc[g] = [];
    acc[g].push(f);
    return acc;
  }, {});

  const navByGroup = navActions.reduce((acc, a) => {
    const g = a.group || 'Outros';
    if (!acc[g]) acc[g] = [];
    acc[g].push(a);
    return acc;
  }, {});

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  return (
    <div className="push-campaign-panel">
      <div className="push-subtabs">
        <button
          type="button"
          className={`tab-btn ${mode === 'compose' ? 'active' : ''}`}
          onClick={() => setMode('compose')}
        >
          <Send sx={{ fontSize: 18 }} /> Enviar push
        </button>
        <button
          type="button"
          className={`tab-btn ${mode === 'history' ? 'active' : ''}`}
          onClick={() => setMode('history')}
        >
          <Campaign sx={{ fontSize: 18 }} /> Histórico push
        </button>
      </div>

      {mode === 'compose' && (
        <div className="tab-content compose-content">
          <div className="compose-grid">
            <div className="admin-card pn-card pn-card--default recipients-card">
              <div className="card-header">
                <People sx={{ fontSize: 24 }} />
                <h3>Destinatários (app com token)</h3>
              </div>

              <div className="recipients-options">
                <label className={`recipient-option ${recipientType === 'all_with_token' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="pushRecipientType"
                    checked={recipientType === 'all_with_token'}
                    onChange={() => setRecipientType('all_with_token')}
                  />
                  <div className="option-content">
                    <PhoneIphone sx={{ fontSize: 20 }} />
                    <div>
                      <strong>Todos com token push</strong>
                      <span>Sem opt-out de marketing</span>
                    </div>
                  </div>
                </label>

                <label className={`recipient-option ${recipientType === 'filter' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="pushRecipientType"
                    checked={recipientType === 'filter'}
                    onChange={() => setRecipientType('filter')}
                  />
                  <div className="option-content">
                    <FilterList sx={{ fontSize: 20 }} />
                    <div>
                      <strong>Filtrar por segmento</strong>
                    </div>
                  </div>
                </label>

                {recipientType === 'filter' && (
                  <select
                    className="admin-input"
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                  >
                    {Object.entries(filtersByGroup).map(([group, items]) => (
                      <optgroup key={group} label={group}>
                        {items.map((f) => (
                          <option key={f.filter} value={f.filter}>
                            {f.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                )}

                <label className={`recipient-option ${recipientType === 'selected' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="pushRecipientType"
                    checked={recipientType === 'selected'}
                    onChange={() => setRecipientType('selected')}
                  />
                  <div className="option-content">
                    <PersonSearch sx={{ fontSize: 20 }} />
                    <strong>IDs específicos</strong>
                  </div>
                </label>

                {recipientType === 'selected' && (
                  <input
                    type="text"
                    className="admin-input"
                    placeholder="Ex: 1, 5, 10"
                    value={selectedUserIds}
                    onChange={(e) => setSelectedUserIds(e.target.value)}
                  />
                )}
              </div>

              <div className="count-section">
                <Button
                  variant="secondary"
                  onClick={countRecipients}
                  loading={countLoading}
                >
                  Contar destinatários
                </Button>
                {recipientCount && (
                  <div className={`count-result ${recipientCount.error ? 'error' : ''}`}>
                    {recipientCount.error ? (
                      <span>{recipientCount.error}</span>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 20 }} />
                        <span>
                          <strong>{recipientCount.total}</strong> — {recipientCount.description}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="admin-card pn-card pn-card--default compose-card">
              <div className="card-header">
                <Campaign sx={{ fontSize: 24 }} />
                <h3>Mensagem push</h3>
              </div>

              <div className="compose-form">
                <Input
                  label="Título *"
                  value={pushForm.title}
                  onChange={(e) => setPushForm({ ...pushForm, title: e.target.value })}
                  maxLength={200}
                  placeholder="Ex: {first_name}, novidade no app!"
                />

                <TextArea
                  label="Mensagem *"
                  rows={5}
                  value={pushForm.body}
                  onChange={(e) => setPushForm({ ...pushForm, body: e.target.value })}
                  placeholder="Corpo da notificação"
                />

                <Select
                  label="Tela ao tocar *"
                  value={pushForm.action}
                  onChange={(e) => setPushForm({ ...pushForm, action: e.target.value })}
                >
                  {Object.entries(navByGroup).map(([group, items]) => (
                    <optgroup key={group} label={group}>
                      {items.map((a) => (
                        <option key={a.action} value={a.action}>
                          {a.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
                <small className="nav-preview">
                  Ao tocar, o usuário será levado para: <strong>{selectedActionLabel}</strong>
                </small>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={pushForm.personalize}
                    onChange={(e) => setPushForm({ ...pushForm, personalize: e.target.checked })}
                  />
                  Personalizar com {'{first_name}'} no título/corpo
                </label>

                {canEdit && (
                  <Button
                    variant="primary"
                    className="send-btn"
                    loading={sending}
                    onClick={() => {
                      const err = validateForm();
                      if (err) {
                        alert(err);
                        return;
                      }
                      setConfirmModal(true);
                    }}
                  >
                    <Send sx={{ fontSize: 18 }} />
                    Enviar campanha push
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {mode === 'history' && (
        <div className="tab-content">
          {historyLoading ? (
            <>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} height={40} style={{ marginBottom: 8 }} />
              ))}
            </>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Título</th>
                  <th>Destino</th>
                  <th>Enviados</th>
                  <th>Falhas</th>
                  <th>Status</th>
                  <th>Data</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {history.map((c) => (
                  <tr key={c.id}>
                    <td>{c.id}</td>
                    <td>{c.title}</td>
                    <td>{c.action_label || c.action}</td>
                    <td>{c.sent_count}/{c.total_recipients}</td>
                    <td>{c.failed_count}</td>
                    <td>{c.status}</td>
                    <td>{formatDate(c.sent_at || c.created_at)}</td>
                    <td>
                      <button type="button" className="admin-btn-link" onClick={() => openCampaignDetail(c.id)}>
                        Detalhe
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {selectedCampaign && campaignDetail && (
            <div className="admin-card pn-card pn-card--default" style={{ marginTop: 16 }}>
              <h4>Campanha #{selectedCampaign}</h4>
              <p>Taxa de sucesso: {campaignDetail.stats?.success_rate_percent}%</p>
              <p>Segmento: {campaignDetail.campaign?.segment_description}</p>
              <button type="button" className="admin-btn-link" onClick={() => setSelectedCampaign(null)}>
                Fechar
              </button>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmModal}
        title="Confirmar envio push"
        message={`Destino: ${selectedActionLabel}. ${
          recipientCount
            ? `${recipientCount.total} destinatários`
            : 'Conte os destinatários antes de confirmar'
        }`}
        confirmLabel="Confirmar"
        onClose={() => setConfirmModal(false)}
        onConfirm={handleConfirmSend}
      />

      <SuccessModal
        open={resultModal.open && resultModal.data?.success}
        title="Campanha enviada"
        message={
          resultModal.data
            ? `Enviados: ${resultModal.data.sent} / ${resultModal.data.total}. Falhas: ${resultModal.data.failed}`
            : ''
        }
        onClose={() => setResultModal({ open: false, data: null })}
      />

      <ErrorModal
        open={resultModal.open && resultModal.data && !resultModal.data.success}
        message={resultModal.data?.error || 'Erro ao enviar campanha'}
        onClose={() => setResultModal({ open: false, data: null })}
      />
    </div>
  );
};

export default PushCampaignPanel;
